import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import FontAwesome from '@react-native-vector-icons/fontawesome';
import { Card } from 'react-native-paper';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useGetANotificationsByUserQuery } from '../redux/services/anotificationsApi';
import { useGetPendingAuthorizationsQuery } from '../redux/services/foauthorizationApi';
import {
  isSoundAlertEnabled,
  playSoundAlert,
  setSoundAlertEnabled,
} from '../utils/soundAlert';
import { ShowToast } from '../Common/common';
import { colors, borderRadius, shadows, spacing, typography } from '../theme/theme';

import T from '../Components/T';
const Notifications: React.FC<UniversalProps> = ({ navigation }) => {
  const gdata = useSelector((state: RootState) => state.gdata);

  const hasFOAccess = gdata.menu?.some(
    (v: any) => v.mn_link === '/status/status' || v.mn_link === '/admin/company'
  );
  const hasAuthPermission =
    hasFOAccess ||
    gdata.menu?.some(
      (v: any) => v.mn_link === '/admin/pending-authorizations'
    );

  const {
    data: notifications = [],
    isLoading: isNotifLoading,
    isFetching: isNotifFetching,
    refetch: refetchNotifs,
  } = useGetANotificationsByUserQuery(undefined, { skip: !gdata.user });

  const {
    data: pendingAuths = [],
    refetch: refetchAuths,
    isFetching: isAuthFetching,
  } = useGetPendingAuthorizationsQuery(undefined, {
    skip: !hasAuthPermission,
  });

  const [soundEnabled, setSoundEnabled] = useState(true);

  useEffect(() => {
    (async () => {
      const enabled = await isSoundAlertEnabled();
      setSoundEnabled(enabled);
    })();
  }, []);

  const handleToggleSound = async () => {
    const nextVal = !soundEnabled;
    setSoundEnabled(nextVal);
    await setSoundAlertEnabled(nextVal);
    if (nextVal) {
      await playSoundAlert(true);
      ShowToast('Sound alerts enabled', 'success');
    } else {
      ShowToast('Sound alerts disabled', 'info');
    }
  };

  const handleRefresh = () => {
    refetchNotifs();
    if (hasAuthPermission) {
      refetchAuths();
    }
  };

  const isRefreshing = isNotifFetching || isAuthFetching;
  const pendingCount = Array.isArray(pendingAuths) ? pendingAuths.length : 0;

  const renderHeader = () => {
    if (pendingCount === 0) return null;

    return (
      <TouchableOpacity
        style={styles.pendingBanner}
        activeOpacity={0.85}
        onPress={() => navigation?.navigate?.('PendingAuthorizations')}>
        <View style={styles.bannerIconCircle}>
          <FontAwesome name="gavel" size={20} color="#EF4444" />
        </View>
        <View style={styles.bannerTextCol}>
          <Text style={styles.bannerTitle}>
            {pendingCount} <T>Pending Authorization</T>{pendingCount > 1 ? 's' : ''}
          </Text>
          <Text style={styles.bannerSubtitle}>
            <T>Front-office approval requests awaiting review</T>
          </Text>
        </View>
        <View style={styles.bannerAction}>
          <Text style={styles.bannerActionText}><T>Review</T></Text>
          <FontAwesome name="chevron-right" size={12} color="#EF4444" style={{ marginLeft: 4 }} />
        </View>
      </TouchableOpacity>
    );
  };

  const renderNotificationItem = ({ item }: { item: bt_enotifications }) => {
    const isUnread = item.nt_isread !== '1';
    const formattedDate = item.nt_cdt
      ? moment(item.nt_cdt).format('DD-MM-YY HH:mm')
      : '';

    return (
      <Card style={[styles.notifCard, isUnread && styles.notifUnreadCard]} elevation={2}>
        <View style={styles.notifContentRow}>
          <View style={[styles.notifIconCircle, isUnread ? styles.unreadCircle : styles.readCircle]}>
            <FontAwesome
              name={isUnread ? 'bell' : 'bell-o'}
              size={16}
              color={isUnread ? '#EF4444' : '#6B7280'}
            />
          </View>

          <View style={styles.notifTextCol}>
            <View style={styles.notifTopRow}>
              <Text style={[styles.notifTitle, isUnread && styles.unreadText]}>
                {item.nt_title || 'System Notification'}
              </Text>
              {isUnread && <View style={styles.unreadDot} />}
            </View>

            <Text style={styles.notifMessage}>{item.nt_message}</Text>

            {Boolean(formattedDate) && (
              <View style={styles.dateRow}>
                <FontAwesome name="clock-o" size={11} color="#9CA3AF" style={{ marginRight: 4 }} />
                <Text style={styles.dateText}>{formattedDate}</Text>
              </View>
            )}
          </View>
        </View>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      {/* Top Bar */}
      <View style={styles.toolbar}>
        <Text style={styles.screenTitle}><T>Notifications</T></Text>

        <View style={styles.toolbarActions}>
          <TouchableOpacity
            style={[
              styles.soundToggleBtn,
              soundEnabled ? styles.soundActive : styles.soundMuted,
            ]}
            onPress={handleToggleSound}
            activeOpacity={0.8}>
            <FontAwesome
              name={soundEnabled ? 'volume-up' : 'volume-off'}
              size={14}
              color={soundEnabled ? '#fff' : '#6B7280'}
              style={{ marginRight: 6 }}
            />
            <Text
              style={[
                styles.soundToggleText,
                { color: soundEnabled ? '#fff' : '#4B5563' },
              ]}>
              {soundEnabled ? 'Sound: ON' : 'Sound: OFF'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.refreshBtn}
            onPress={handleRefresh}
            disabled={isRefreshing}
            activeOpacity={0.7}>
            <FontAwesome
              name="refresh"
              size={14}
              color={colors.primary}
            />
          </TouchableOpacity>
        </View>
      </View>

      {/* Main List */}
      {isNotifLoading ? (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}><T>Loading notifications...</T></Text>
        </View>
      ) : (
        <FlatList
          data={notifications}
          keyExtractor={(item, idx) =>
            item.nt_id ? item.nt_id.toString() : idx.toString()
          }
          renderItem={renderNotificationItem}
          ListHeaderComponent={renderHeader}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <View style={styles.emptyIconCircle}>
                <FontAwesome name="bell-slash-o" size={42} color="#9CA3AF" />
              </View>
              <Text style={styles.emptyTitle}><T>No Notifications</T></Text>
              <Text style={styles.emptySubtitle}>
                <T>You have no software notifications at this moment.</T>
              </Text>
            </View>
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderColor: '#E5E7EB',
    ...shadows.sm,
  },
  screenTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1F2937',
  },
  toolbarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  soundToggleBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: borderRadius.md,
  },
  soundActive: {
    backgroundColor: '#03C988',
  },
  soundMuted: {
    backgroundColor: '#E5E7EB',
  },
  soundToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  refreshBtn: {
    padding: 8,
    borderRadius: borderRadius.md,
    backgroundColor: '#F3F4F6',
  },
  listContent: {
    padding: 12,
    paddingBottom: 80,
  },
  pendingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FCA5A5',
    borderRadius: borderRadius.lg,
    padding: 12,
    marginBottom: 12,
    ...shadows.sm,
  },
  bannerIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  bannerTextCol: {
    flex: 1,
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#991B1B',
    marginBottom: 2,
  },
  bannerSubtitle: {
    fontSize: 11,
    color: '#B91C1C',
  },
  bannerAction: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FCA5A5',
  },
  bannerActionText: {
    color: '#EF4444',
    fontSize: 11,
    fontWeight: '700',
  },
  notifCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.lg,
    marginBottom: 10,
    padding: 12,
  },
  notifUnreadCard: {
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  notifContentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  notifIconCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  unreadCircle: {
    backgroundColor: '#FEE2E2',
  },
  readCircle: {
    backgroundColor: '#F3F4F6',
  },
  notifTextCol: {
    flex: 1,
  },
  notifTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  notifTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    flex: 1,
  },
  unreadText: {
    fontWeight: '700',
    color: '#111827',
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#EF4444',
    marginLeft: 6,
  },
  notifMessage: {
    fontSize: 13,
    color: '#4B5563',
    lineHeight: 18,
    marginBottom: 6,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateText: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  loadingText: {
    marginTop: 12,
    color: '#6B7280',
    fontSize: 14,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyIconCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#374151',
    marginBottom: 4,
  },
  emptySubtitle: {
    fontSize: 13,
    color: '#6B7280',
    textAlign: 'center',
  },
});

export default Notifications;
